import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import nodeTypesIcon from "../../assets/network-graph/node-types.png";
import investorLinksIcon from "../../assets/network-graph/investor-links.png";
import peopleIcon from "../../assets/network-graph/people.png";
import {
  appendListParams,
  ControlCardHeader,
  getEndpointId,
  graphLinkKey,
  hexToRgba,
  InfoHint,
  LabelWithInfo,
  normalizeKey,
  prettifyCategory,
} from "./graphShared";
import "./NetworkGraph.css";

function formatConfidence(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return `${Math.round(value * 100)}%`;
}

function normalizePersonCategoryKey(value) {
  return String(value || "").trim().toLowerCase();
}

function getPersonCategoryStyle(category) {
  switch (normalizePersonCategoryKey(category)) {
    case "founder":
      return { fill: "#8a7400", chipBg: "#f5edbf", chipText: "#6f5d00" };
    case "executive":
      return { fill: "#b39a14", chipBg: "#f9f0c9", chipText: "#836f05" };
    case "board":
      return { fill: "#ceb54d", chipBg: "#fbf4da", chipText: "#8b7716" };
    case "advisor":
      return { fill: "#e3d28b", chipBg: "#fdf8e8", chipText: "#8f7a27" };
    default:
      return { fill: "#f3ecc8", chipBg: "#fffaf0", chipText: "#8c7a35" };
  }
}

function getPersonCategoryChipSx(category) {
  const style = getPersonCategoryStyle(category);
  return {
    bgcolor: style.chipBg,
    color: style.chipText,
  };
}

function getNodeDisplayColor(node) {
  if (node?.nodeType === "person") {
    return getPersonCategoryStyle(node.personCategory).fill;
  }
  return node?.color || "#9eb0c5";
}

const PEOPLE_CATEGORY_LEGEND = [
  { key: "Founder", help: "Founder and co-founder roles." },
  {
    key: "Executive",
    help: "Senior operating leaders such as CEO, CTO, CFO, COO, VP, and president.",
  },
  { key: "Board", help: "Board, chair, and governance roles." },
  { key: "Advisor", help: "Scientific, strategic, and general advisory roles." },
  { key: "Other", help: "Employee and all residual or ambiguous people roles." },
];

function buildNetworkGraphPath(filters, topology) {
  const params = new URLSearchParams();
  params.set("tab", filters.currentTab || "OVERVIEW");
  if (filters.searchTerm?.trim()) params.set("search", filters.searchTerm.trim());
  appendListParams(params, "companyName", filters.selectedGraphCompanyNames);
  if (filters.updatedWindowFrom) params.set("from", filters.updatedWindowFrom);
  if (filters.updatedWindowToExclusive) params.set("to", filters.updatedWindowToExclusive);
  appendListParams(params, "industry", filters.selectedIndustry);
  appendListParams(params, "country", filters.selectedCountry);
  appendListParams(params, "stage", filters.selectedStage);
  appendListParams(params, "companyAge", filters.selectedCompanyAge);
  appendListParams(params, "updateCategory", filters.selectedUpdateCategory);
  appendListParams(params, "estimatedRevenueRange", filters.selectedEstimatedRevenueRange);
  appendListParams(params, "investorType", filters.selectedInvestorType);
  appendListParams(params, "fundingPeriod", filters.selectedFundingPeriod);
  appendListParams(params, "fundingYear", filters.selectedFundingYear);
  appendListParams(params, "leadStatus", filters.selectedLeadStatus);
  if (filters.matchedState && filters.matchedState !== "all") {
    params.set("matchedState", filters.matchedState);
  }
  if (filters.clientState && filters.clientState !== "all") {
    params.set("clientState", filters.clientState);
  }
  if (filters.selectedManualChange) params.set("manualChange", filters.selectedManualChange);
  if (filters.assignmentView) params.set("assignmentScope", filters.assignmentView);
  params.set("showStartup", topology.showStartup ? "true" : "false");
  params.set("showNonStartup", topology.showNonStartup ? "true" : "false");
  params.set("showInvestors", topology.showInvestors ? "true" : "false");
  params.set("showLeadLinks", topology.showLeadLinks ? "true" : "false");
  params.set("showTop5Links", topology.showTop5Links ? "true" : "false");
  params.set("includePeople", topology.includePeople ? "true" : "false");
  params.set(
    "minCompaniesPerInvestor",
    String(Math.max(0, topology.minCompaniesPerInvestor || 0))
  );
  params.set(
    "minCompaniesPerPerson",
    String(Math.max(0, topology.minCompaniesPerPerson || 0))
  );
  params.set(
    "minInvestorsPerCompany",
    String(Math.max(0, topology.minInvestorsPerCompany || 0))
  );
  appendListParams(params, "personCategory", topology.selectedPersonCategories);
  return `/api/network/graph?${params.toString()}`;
}

export default function NetworkGraph({
  apiEnabled,
  companies,
  filters,
  getApiJson,
  setSidebarCompanyNames,
}) {
  const graphRef = useRef(null);
  const canvasRef = useRef(null);
  const lastNodeClickRef = useRef({ id: null, timestamp: 0 });
  const [graph, setGraph] = useState({ nodes: [], links: [], meta: null });
  const [graphRevision, setGraphRevision] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [searchedNodeId, setSearchedNodeId] = useState(null);
  const [companySearch, setCompanySearch] = useState("");
  const [searchNotice, setSearchNotice] = useState("");

  const [includePeople, setIncludePeople] = useState(false);
  const [selectedPersonCategories, setSelectedPersonCategories] = useState([]);
  const [showStartup, setShowStartup] = useState(true);
  const [showNonStartup, setShowNonStartup] = useState(true);
  const [showInvestors, setShowInvestors] = useState(true);
  const [showLeadLinks, setShowLeadLinks] = useState(true);
  const [showTop5Links, setShowTop5Links] = useState(true);
  const [minInvestorCompanyCount, setMinInvestorCompanyCount] = useState(1);
  const [minPersonCompanyCount, setMinPersonCompanyCount] = useState(1);
  const [minInvestorsPerCompany, setMinInvestorsPerCompany] = useState(0);
  const [nodeScale, setNodeScale] = useState(1);

  const companyIndex = useMemo(() => {
    const nextIndex = {};
    (companies || []).forEach((company) => {
      nextIndex[normalizeKey(company["Organization Name"])] = company;
    });
    return nextIndex;
  }, [companies]);

  const graphRequestPath = useMemo(
    () =>
      buildNetworkGraphPath(filters, {
        includePeople,
        selectedPersonCategories,
        showStartup,
        showNonStartup,
        showInvestors,
        showLeadLinks,
        showTop5Links,
        minCompaniesPerInvestor: minInvestorCompanyCount,
        minCompaniesPerPerson: minPersonCompanyCount,
        minInvestorsPerCompany,
      }),
    [
      filters,
      includePeople,
      selectedPersonCategories,
      showStartup,
      showNonStartup,
      showInvestors,
      showLeadLinks,
      showTop5Links,
      minInvestorCompanyCount,
      minPersonCompanyCount,
      minInvestorsPerCompany,
    ]
  );

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const updateCanvasSize = (nextWidth, nextHeight) => {
      const width = Math.max(0, Math.round(nextWidth || 0));
      const height = Math.max(0, Math.round(nextHeight || 0));
      setCanvasSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height }
      );
    };

    const element = canvasRef.current;
    const initialRect = element.getBoundingClientRect();
    updateCanvasSize(initialRect.width, initialRect.height);

    if (typeof ResizeObserver === "undefined") {
      const handleResize = () => {
        const rect = element.getBoundingClientRect();
        updateCanvasSize(rect.width, rect.height);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateCanvasSize(entry.contentRect.width, entry.contentRect.height);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!apiEnabled || typeof getApiJson !== "function") return undefined;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getApiJson(graphRequestPath);
        if (!cancelled) {
          setSelectedNode(null);
          setHoveredNodeId(null);
          setSearchedNodeId(null);
          setGraph({
            nodes: data?.nodes || [],
            links: data?.links || [],
            meta: data?.meta || null,
          });
          setGraphRevision((current) => current + 1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Network graph loading failed: ${err.message}`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    apiEnabled,
    graphRequestPath,
    getApiJson,
  ]);

  useEffect(() => {
    if (!includePeople && selectedPersonCategories.length > 0) {
      setSelectedPersonCategories([]);
    }
  }, [includePeople, selectedPersonCategories.length]);

  useEffect(() => {
    const allowedCategories = PEOPLE_CATEGORY_LEGEND.map((item) => item.key);
    if (!selectedPersonCategories.length) return;
    const nextSelection = selectedPersonCategories.filter((value) =>
      allowedCategories.includes(value)
    );
    if (nextSelection.length !== selectedPersonCategories.length) {
      setSelectedPersonCategories(nextSelection);
    }
  }, [selectedPersonCategories]);

  const visibleGraph = graph;

  useEffect(() => {
    if (selectedNode && !visibleGraph.nodes.some((node) => node.id === selectedNode.id)) {
      setSelectedNode(null);
    }
  }, [selectedNode, visibleGraph.nodes]);

  useEffect(() => {
    if (hoveredNodeId && !visibleGraph.nodes.some((node) => node.id === hoveredNodeId)) {
      setHoveredNodeId(null);
    }
  }, [hoveredNodeId, visibleGraph.nodes]);

  const interactionNodeId = hoveredNodeId || selectedNode?.id || searchedNodeId || null;

  const hoverContext = useMemo(() => {
    if (!interactionNodeId) return null;

    const highlightedNodeIds = new Set([interactionNodeId]);
    const highlightedLinkKeys = new Set();

    visibleGraph.links.forEach((link) => {
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      if (sourceId === interactionNodeId || targetId === interactionNodeId) {
        highlightedNodeIds.add(sourceId);
        highlightedNodeIds.add(targetId);
        highlightedLinkKeys.add(graphLinkKey(link));
      }
    });

    return { highlightedNodeIds, highlightedLinkKeys };
  }, [interactionNodeId, visibleGraph.links]);

  const nodePaint = useCallback(
    (node, ctx, globalScale) => {
      const radius = (node.size || 8) * nodeScale;
      const fontSize = Math.max(8, 12 / globalScale);
      const isHighlighted = !hoverContext || hoverContext.highlightedNodeIds.has(node.id);
      const isFocusNode = node.id === interactionNodeId;
      const isSelected = selectedNode?.id === node.id;
      const isHovered = hoveredNodeId === node.id;
      const nodeColor = getNodeDisplayColor(node);

      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = isHighlighted ? nodeColor : hexToRgba(nodeColor, 0.16);
      ctx.fill();
      ctx.lineWidth = isSelected ? 2.8 : isHovered ? 2 : 1;
      ctx.strokeStyle = isSelected ? "#0f172a" : isHovered ? "#1f4d88" : "#cbd5e1";
      ctx.stroke();

      if (isFocusNode) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI, false);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgba(31,77,136,0.75)";
        ctx.stroke();
      }

      const label = node.label || node.id;
      ctx.font = `${fontSize}px "Segoe UI", Tahoma, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = isHighlighted ? "#243042" : "rgba(36, 48, 66, 0.32)";
      ctx.fillText(label, node.x, node.y + radius + 2);
    },
    [hoverContext, hoveredNodeId, interactionNodeId, nodeScale, selectedNode]
  );

  const focusGraphOnNode = useCallback((node, duration = 600) => {
    if (!node || !graphRef.current) return;
    if (typeof node.x === "number" && typeof node.y === "number") {
      graphRef.current.centerAt(node.x, node.y, duration);
      graphRef.current.zoom(2.1, duration);
    }
  }, []);

  const fitGraphToVisibleArea = useCallback((duration = 500) => {
    if (!graphRef.current || !canvasRef.current || !visibleGraph.nodes.length) return;

    const bbox = graphRef.current.getGraphBbox?.();
    if (!bbox?.x || !bbox?.y) {
      graphRef.current.zoomToFit(duration, 60);
      return;
    }

    const graphWidth = Math.max(1, bbox.x[1] - bbox.x[0]);
    const graphHeight = Math.max(1, bbox.y[1] - bbox.y[0]);
    const canvasRect = canvasRef.current.getBoundingClientRect();

    const tabsElement = document.querySelector("[data-right-panel-tabs='true']");
    let coveredRightWidth = 0;
    if (tabsElement) {
      const tabsRect = tabsElement.getBoundingClientRect();
      const verticalOverlap =
        Math.min(canvasRect.bottom, tabsRect.bottom) - Math.max(canvasRect.top, tabsRect.top);
      if (verticalOverlap > 0) {
        coveredRightWidth = Math.max(0, canvasRect.right - tabsRect.left + 16);
      }
    }

    const padding = 60;
    const availableWidth = Math.max(140, canvasRect.width - coveredRightWidth - padding * 2);
    const availableHeight = Math.max(140, canvasRect.height - padding * 2);
    const zoom = Math.min(availableWidth / graphWidth, availableHeight / graphHeight);

    if (!Number.isFinite(zoom) || zoom <= 0) {
      graphRef.current.zoomToFit(duration, 60);
      return;
    }

    const centerX = (bbox.x[0] + bbox.x[1]) / 2 - coveredRightWidth / (2 * zoom);
    const centerY = (bbox.y[0] + bbox.y[1]) / 2;
    graphRef.current.centerAt(centerX, centerY, duration);
    graphRef.current.zoom(zoom, duration);
  }, [visibleGraph.nodes.length]);

  const scheduleFitGraphToVisibleArea = useCallback((duration = 500) => {
    if (typeof window === "undefined") {
      fitGraphToVisibleArea(duration);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        fitGraphToVisibleArea(duration);
      });
    });
  }, [fitGraphToVisibleArea]);

  const activeSelectedNode = useMemo(() => {
    if (!selectedNode) return null;
    return visibleGraph.nodes.find((node) => node.id === selectedNode.id) || selectedNode;
  }, [selectedNode, visibleGraph.nodes]);

  const handleResetZoom = useCallback(() => {
    if (activeSelectedNode) {
      focusGraphOnNode(activeSelectedNode, 500);
      return;
    }
    scheduleFitGraphToVisibleArea(500);
  }, [activeSelectedNode, focusGraphOnNode, scheduleFitGraphToVisibleArea]);

  const handleClearFocus = useCallback(() => {
    setCompanySearch("");
    setSearchNotice("");
    setSearchedNodeId(null);
    setHoveredNodeId(null);
    setSelectedNode(null);
    if (typeof setSidebarCompanyNames === "function") {
      setSidebarCompanyNames([]);
    }
    scheduleFitGraphToVisibleArea(500);
  }, [
    scheduleFitGraphToVisibleArea,
    setSidebarCompanyNames,
  ]);

  const handleCompanySearch = useCallback(
    (event) => {
      event.preventDefault();
      const query = companySearch.trim().toLowerCase();
      if (!query) {
        setSearchedNodeId(null);
        setSearchNotice("");
        return;
      }

      const visibleCompanies = visibleGraph.nodes.filter((node) => node.nodeType === "company");
      const found = visibleCompanies.find((node) =>
        String(node.label || "").toLowerCase().includes(query)
      );

      if (!found) {
        setSearchedNodeId(null);
        setSearchNotice("No visible company matches this search.");
        return;
      }

      setSelectedNode(found);
      setHoveredNodeId(null);
      setSearchedNodeId(found.id);
      setCompanySearch(found.label || "");
      setSearchNotice(`Highlighted: ${found.label}`);
    },
    [companySearch, visibleGraph.nodes]
  );

  const getVisibleConnectedCompanyNames = useCallback(
    (nodeId) => {
      const companyNodeById = new Map(
        visibleGraph.nodes
          .filter((node) => node.nodeType === "company")
          .map((node) => [node.id, node])
      );
      const seen = new Set();
      const companyNames = [];

      visibleGraph.links.forEach((link) => {
        const sourceId = getEndpointId(link.source);
        const targetId = getEndpointId(link.target);
        if (sourceId !== nodeId && targetId !== nodeId) return;

        const otherId = sourceId === nodeId ? targetId : sourceId;
        const companyNode = companyNodeById.get(otherId);
        if (!companyNode) return;

        const label = String(companyNode.label || companyNode.id || "").trim();
        const key = normalizeKey(label);
        if (!key || seen.has(key)) return;

        seen.add(key);
        companyNames.push(label);
      });

      return companyNames.sort((a, b) => a.localeCompare(b));
    },
    [visibleGraph.links, visibleGraph.nodes]
  );

  useEffect(() => {
    if (!graphRef.current) return;
    const chargeForce = graphRef.current.d3Force("charge");
    if (chargeForce?.strength) chargeForce.strength(-150);
    const linkForce = graphRef.current.d3Force("link");
    if (linkForce?.distance) {
      linkForce.distance((link) => (link.linkType === "lead" ? 95 : link.linkType === "person" ? 80 : 70));
    }
    if (typeof graphRef.current.d3ReheatSimulation === "function") {
      graphRef.current.d3ReheatSimulation();
    }
  }, [graphRevision]);

  useEffect(() => {
    if (!activeSelectedNode || canvasSize.width <= 0 || canvasSize.height <= 0) return undefined;

    let rafId = 0;
    let nestedRafId = 0;
    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        const visibleSelectedNode = visibleGraph.nodes.find((node) => node.id === activeSelectedNode.id);
        focusGraphOnNode(visibleSelectedNode || activeSelectedNode, 450);
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
    };
  }, [activeSelectedNode, canvasSize.height, canvasSize.width, focusGraphOnNode, visibleGraph.nodes]);

  useEffect(() => {
    if (
      graphRevision === 0 ||
      activeSelectedNode ||
      canvasSize.width <= 0 ||
      canvasSize.height <= 0 ||
      !visibleGraph.nodes.length
    ) {
      return undefined;
    }

    let rafId = 0;
    let nestedRafId = 0;
    let timeoutId = 0;
    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          fitGraphToVisibleArea(350);
        }, 140);
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
      window.clearTimeout(timeoutId);
    };
  }, [activeSelectedNode, canvasSize.height, canvasSize.width, fitGraphToVisibleArea, graphRevision, visibleGraph.nodes.length]);

  const selectedCompany = useMemo(() => {
    if (!activeSelectedNode || activeSelectedNode.nodeType !== "company") return null;
    return companyIndex[normalizeKey(activeSelectedNode.label)] || null;
  }, [activeSelectedNode, companyIndex]);

  const selectedCompanyPeople = useMemo(() => {
    if (!activeSelectedNode || activeSelectedNode.nodeType !== "company") return [];

    const personNodeById = new Map(
      (visibleGraph.nodes || [])
        .filter((node) => node.nodeType === "person")
        .map((node) => [node.id, node])
    );
    const people = [];
    const seen = new Set();

    (visibleGraph.links || []).forEach((link) => {
      if (link.linkType !== "person") return;

      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      const personId =
        sourceId === activeSelectedNode.id
          ? targetId
          : targetId === activeSelectedNode.id
          ? sourceId
          : null;

      if (!personId || seen.has(personId)) return;

      const personNode = personNodeById.get(personId);
      if (!personNode) return;

      seen.add(personId);
      people.push(personNode);
    });

    return people;
  }, [activeSelectedNode, visibleGraph.links, visibleGraph.nodes]);

  if (!apiEnabled) {
    return (
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Network View Requires API Mode
        </Typography>
        <Typography sx={{ color: "#475569" }}>
          This graph is server-driven. Start the client with `REACT_APP_DATA_SOURCE=api`
          and the backend endpoint `GET /api/network/graph`.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Paper sx={{ p: 0.75, borderRadius: 2, mb: 0.75 }}>
        <Box className="network-graph__toolbar-topline">
          <Typography className="network-graph__intro">
            Left workspace filters choose the company slice first. These compact controls only change graph topology.
          </Typography>
          <InfoHint title="Anything that changes which nodes or links exist calls the backend again. Panning, zooming, focus, and Find stay local in the browser." />
        </Box>
        <Box className="network-graph__control-grid">
          <Box className="network-graph__control-section">
            <ControlCardHeader
              icon={nodeTypesIcon}
              title="Company"
              info="Choose which company families the API should include in the returned network."
            />
            <Typography className="network-graph__control-help network-graph__control-help--tight">
              Which company families come back from the API.
            </Typography>
            <Box className="network-graph__control-stack">
              <FormControlLabel
                control={
                  <Checkbox checked={showStartup} onChange={(e) => setShowStartup(e.target.checked)} />
                }
                label={
                  <LabelWithInfo
                    label="Startup companies"
                    color="#1f8f46"
                    info="Early-stage companies from Crunchbase funding stage: Pre-Seed, Seed, and the Series A family."
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showNonStartup}
                    onChange={(e) => setShowNonStartup(e.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Non-startup companies"
                    color="#c56a1d"
                    info="All remaining companies, including later-stage and unknown or uncategorised stage values."
                  />
                }
              />
            </Box>
            <Typography className="network-graph__micro-note">
              Startup = Pre-Seed, Seed, and Series A family.
            </Typography>
          </Box>

          <Box className="network-graph__control-section">
            <ControlCardHeader
              icon={investorLinksIcon}
              title="Investors"
              info="Choose which investor nodes and Crunchbase investor-company link layers the API should return."
            />
            <Typography className="network-graph__control-help network-graph__control-help--tight">
              Add investor nodes and investor-company link layers.
            </Typography>
            <Box className="network-graph__control-stack">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInvestors}
                    onChange={(e) => setShowInvestors(e.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Show investors"
                    color="#8eb8ff"
                    info="Counterparty investor nodes connected to the returned companies."
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showLeadLinks}
                    onChange={(e) => setShowLeadLinks(e.target.checked)}
                    disabled={!showInvestors}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Primary investor links"
                    info="Draw links from the 'Lead Investors' field. This is treated as the stronger investor-company relationship."
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTop5Links}
                    onChange={(e) => setShowTop5Links(e.target.checked)}
                    disabled={!showInvestors}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Top 5 investor links"
                    info="Draw links from the 'Top 5 Investors' field as the secondary investor-company relationship."
                  />
                }
              />
            </Box>
            <Box className="network-graph__compact-field-grid">
              <TextField
                label="Min co. / investor"
                type="number"
                size="small"
                value={minInvestorCompanyCount}
                onChange={(e) => setMinInvestorCompanyCount(Math.max(0, Number(e.target.value || 0)))}
                className="network-graph__mini-field"
                disabled={!showInvestors}
              />
              <TextField
                label="Min inv. / company"
                type="number"
                size="small"
                value={minInvestorsPerCompany}
                onChange={(e) => setMinInvestorsPerCompany(Math.max(0, Number(e.target.value || 0)))}
                className="network-graph__mini-field"
                disabled={!showInvestors}
              />
            </Box>
          </Box>

          <Box className="network-graph__control-section">
            <ControlCardHeader
              icon={peopleIcon}
              title="People"
              info="Add stored Crunchbase people extractions, plus the person-category filter used by the graph."
            />
            <Typography className="network-graph__control-help network-graph__control-help--tight">
              Stored Crunchbase people extractions with fixed graph categories.
            </Typography>
            <Box className="network-graph__control-stack">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includePeople}
                    onChange={(e) => setIncludePeople(e.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Include people"
                    info="Return person nodes from stored Crunchbase extraction data."
                  />
                }
              />
            </Box>
            <Box className="network-graph__compact-field-grid network-graph__compact-field-grid--people">
              <FormControl size="small" disabled={!includePeople}>
                <InputLabel>People categories</InputLabel>
                <Select
                  multiple
                  value={selectedPersonCategories}
                  onChange={(e) =>
                    setSelectedPersonCategories(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="People categories" />}
                  renderValue={(selected) =>
                    selected.length
                      ? selected.map((value) => prettifyCategory(value)).join(", ")
                      : "All"
                  }
                >
                  {PEOPLE_CATEGORY_LEGEND.map((category) => (
                    <MenuItem key={category.key} value={category.key}>
                      <Checkbox checked={selectedPersonCategories.includes(category.key)} />
                      <Chip
                        label={category.key}
                        size="small"
                        sx={getPersonCategoryChipSx(category.key)}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Min co. / person"
                type="number"
                size="small"
                value={minPersonCompanyCount}
                onChange={(e) =>
                  setMinPersonCompanyCount(Math.max(0, Number(e.target.value || 0)))
                }
                className="network-graph__mini-field"
                disabled={!includePeople}
              />
            </Box>
            <Typography className="network-graph__micro-note">
              Category colors are shown inside the dropdown.
            </Typography>
          </Box>
        </Box>

        <Box className="network-graph__toolbar-footer">
          <Box className="network-graph__focus-inline">
            <Box component="form" onSubmit={handleCompanySearch} className="network-graph__search">
              <TextField
                label="Find in graph"
                size="small"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
              />
              <Button type="submit" variant="contained">Find</Button>
              <Button variant="outlined" onClick={handleClearFocus}>
                Clear
              </Button>
              <Button variant="outlined" onClick={handleResetZoom}>Reset zoom</Button>
            </Box>
            <Typography className="network-graph__interaction-note">
              {searchNotice ||
                "Click explores locally. Double-click a company to filter that company; double-click an investor to filter the visible connected companies."}
            </Typography>
          </Box>
          <Box className="network-graph__read-inline">
            <Typography className="network-graph__read-line network-graph__read-line--inline">
              <strong>How to read:</strong> size: company=employees, investor/people=visible companies. Shade=funding within startup/non-startup.
            </Typography>
            <Box className="network-graph__node-scale">
              <Typography className="network-graph__node-scale-label">
                Node size {nodeScale.toFixed(1)}x
              </Typography>
              <Slider
                size="small"
                min={0.6}
                max={3}
                step={0.1}
                value={nodeScale}
                onChange={(_, value) => setNodeScale(Array.isArray(value) ? value[0] : value)}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box className={`network-graph__layout${activeSelectedNode ? " network-graph__layout--detail" : ""}`}>
        <Paper ref={canvasRef} className="network-graph__canvas">
          {loading && (
            <Box className="network-graph__overlay">
              <CircularProgress size={22} />
              <Typography>Refreshing network...</Typography>
            </Box>
          )}
          {error ? <Box className="network-graph__error">{error}</Box> : null}
          <ForceGraph2D
            key={graphRevision}
            ref={graphRef}
            width={Math.max(320, canvasSize.width)}
            height={Math.max(520, canvasSize.height)}
            graphData={visibleGraph}
            nodeLabel={(node) => node.label}
            nodeCanvasObject={nodePaint}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, (node.size || 8) * nodeScale + 6, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
            linkColor={(link) => {
              const baseColor =
                link.linkType === "lead"
                  ? "#4b74c2"
                  : link.linkType === "person"
                  ? "#c4ad37"
                  : "#b2bfd1";
              if (!hoverContext) return baseColor;
              return hoverContext.highlightedLinkKeys.has(graphLinkKey(link))
                ? baseColor
                : hexToRgba(baseColor, 0.12);
            }}
            linkWidth={(link) => {
              if (hoverContext?.highlightedLinkKeys.has(graphLinkKey(link))) {
                return link.linkType === "lead" ? 2.2 : 1.5;
              }
              return link.linkType === "lead" ? 1.6 : 1;
            }}
            linkDirectionalParticles={(link) => (link.linkType === "lead" ? 2 : 0)}
            linkDirectionalParticleWidth={1.2}
            d3VelocityDecay={0.28}
            cooldownTicks={120}
            onNodeClick={(node) => {
              const now = Date.now();
              const isSameNodeDoubleClick =
                lastNodeClickRef.current.id === node.id &&
                now - lastNodeClickRef.current.timestamp < 325;

              lastNodeClickRef.current = { id: node.id, timestamp: now };
              setSelectedNode(node);
              setSearchedNodeId(node.id);

              if (!isSameNodeDoubleClick || typeof setSidebarCompanyNames !== "function") {
                return;
              }

              if (node.nodeType === "company") {
                const companyName = String(node.label || "").trim();
                if (companyName) {
                  setSidebarCompanyNames([companyName]);
                  setSearchNotice(`Filtered workspace to: ${companyName}`);
                }
                return;
              }

              if (node.nodeType === "investor") {
                const companyNames = getVisibleConnectedCompanyNames(node.id);
                if (companyNames.length > 0) {
                  setSidebarCompanyNames(companyNames);
                  setSearchNotice(
                    `Filtered workspace to ${companyNames.length} visible companies connected to ${node.label}.`
                  );
                } else {
                  setSearchNotice(`No visible companies are connected to ${node.label}.`);
                }
              }
            }}
            onNodeHover={(node) => setHoveredNodeId(node?.id || null)}
            onBackgroundClick={() => { setSelectedNode(null); setHoveredNodeId(null); }}
            backgroundColor="#f8fbff"
          />
        </Paper>

        <Box className="network-graph__side">
          <Paper className="network-graph__card">
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Visible Metrics</Typography>
            <Typography>Companies: <strong>{visibleGraph.nodes.filter((node) => node.nodeType === "company").length}</strong></Typography>
            <Typography>Investors: <strong>{visibleGraph.nodes.filter((node) => node.nodeType === "investor").length}</strong></Typography>
            <Typography>People: <strong>{visibleGraph.nodes.filter((node) => node.nodeType === "person").length}</strong></Typography>
            <Typography>Links: <strong>{visibleGraph.links.length}</strong></Typography>
            <Typography sx={{ mt: 1 }}>Backend graph already includes the current graph topology filters.</Typography>
          </Paper>
        </Box>

        {activeSelectedNode ? (
          <Paper className="network-graph__detail">
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {activeSelectedNode.label}
            </Typography>

            {activeSelectedNode.nodeType === "company" && selectedCompany ? (
              <Box className="network-graph__detail-list">
                <Typography><strong>Lead type:</strong> {activeSelectedNode.leadType || "Unknown"}</Typography>
                <Typography><strong>Graph category:</strong> {prettifyCategory(activeSelectedNode.companyCategory)}</Typography>
                <Typography><strong>HQ:</strong> {selectedCompany["Headquarters Location"] || "Unknown"}</Typography>
                <Typography><strong>Stage:</strong> {selectedCompany["Investment Stage"] || "Unknown"}</Typography>
                <Typography><strong>Visible investors:</strong> {activeSelectedNode.investorCompanyCount ?? 0}</Typography>
                <Typography><strong>Last funding:</strong> {selectedCompany["Last Funding Date"] || "Unknown"}</Typography>
                <Typography><strong>Funding total:</strong> {selectedCompany["Total Funding Amount"] || "Unknown"}</Typography>
                <Typography><strong>Lead investors:</strong> {selectedCompany["Lead Investors"] || "Unknown"}</Typography>
                <Typography><strong>Top 5 investors:</strong> {selectedCompany["Top 5 Investors"] || "Unknown"}</Typography>
                <Typography><strong>Description:</strong> {selectedCompany["Description"] || "Unknown"}</Typography>
                {selectedCompanyPeople.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Extracted people</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selectedCompanyPeople.slice(0, 10).map((person) => (
                        <Chip
                          key={person.id}
                          label={`${person.label} (${prettifyCategory(person.personCategory)})`}
                          size="small"
                          sx={getPersonCategoryChipSx(person.personCategory)}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ) : activeSelectedNode.nodeType === "person" ? (
              <Box className="network-graph__detail-list">
                {activeSelectedNode.imageUrl ? (
                  <Box
                    component="img"
                    src={activeSelectedNode.imageUrl}
                    alt={activeSelectedNode.label}
                    className="network-graph__detail-image"
                  />
                ) : null}
                <Typography><strong>Type:</strong> Person</Typography>
                {activeSelectedNode.personCategory ? (
                  <Typography><strong>Category:</strong> {prettifyCategory(activeSelectedNode.personCategory)}</Typography>
                ) : null}
                {activeSelectedNode.personTitle ? (
                  <Typography><strong>Title:</strong> {activeSelectedNode.personTitle}</Typography>
                ) : null}
                {activeSelectedNode.relationshipToCompany ? (
                  <Typography><strong>Relationship:</strong> {activeSelectedNode.relationshipToCompany}</Typography>
                ) : null}
                {typeof activeSelectedNode.companyCount === "number" ? (
                  <Typography><strong>Visible connected companies:</strong> {activeSelectedNode.companyCount}</Typography>
                ) : null}
                {formatConfidence(activeSelectedNode.confidence) ? (
                  <Typography><strong>Extraction confidence:</strong> {formatConfidence(activeSelectedNode.confidence)}</Typography>
                ) : null}
                {activeSelectedNode.profileUrl ? (
                  <Typography component="a" href={activeSelectedNode.profileUrl} target="_blank" rel="noreferrer">
                    Open profile
                  </Typography>
                ) : null}
              </Box>
            ) : (
              <Box className="network-graph__detail-list">
                <Typography><strong>Type:</strong> {prettifyCategory(activeSelectedNode.nodeType)}</Typography>
                {typeof activeSelectedNode.investorCompanyCount === "number" ? (
                  <Typography><strong>Visible connected companies:</strong> {activeSelectedNode.investorCompanyCount}</Typography>
                ) : null}
                {activeSelectedNode.profileUrl ? (
                  <Typography component="a" href={activeSelectedNode.profileUrl} target="_blank" rel="noreferrer">
                    Open profile
                  </Typography>
                ) : null}
              </Box>
            )}
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
}
